import { useState } from "react";
import { MessageCircle, Mail, Clock, Bug, CheckCircle } from "lucide-react";
import { logError } from "../lib/errorLogger";
import SimpleNavigation from "./SimpleNavigation";

const faqs = [
  {
    question: "When will the app launch?",
    answer:
      "We're currently in beta testing. Beta testers will get early access, and we'll announce our official launch date soon!",
  },
  {
    question: "Is Bookshelf really free?",
    answer:
      "Yes! Bookshelf is ad-free and we don't sell your data. We're exploring sustainable funding models that keep the app accessible to all readers.",
  },
  {
    question: "Can I suggest features?",
    answer:
      "Absolutely! We love hearing from our users. Send us your ideas through this contact form or join our beta community.",
  },
  {
    question: "Do you offer partnerships?",
    answer:
      "We're open to partnerships that align with our values. Please reach out with details about your organization and proposal.",
  },
];

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const validate = (form: FormState): FormErrors => {
  const errors: FormErrors = {};
  if (!form.name.trim()) errors.name = "Name is required.";
  else if (form.name.trim().length > 100) errors.name = "Name is too long.";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!form.email.trim()) errors.email = "Email is required.";
  else if (!emailRegex.test(form.email.trim()))
    errors.email = "Enter a valid email address.";

  if (!form.subject.trim()) errors.subject = "Subject is required.";
  else if (form.subject.trim().length > 200)
    errors.subject = "Subject is too long.";

  if (!form.message.trim()) errors.message = "Message is required.";
  else if (form.message.trim().length > 5000)
    errors.message = "Message must be under 5000 characters.";

  return errors;
};

export default function Contact() {
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = validate(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setServerError("");

    try {
      const response = await fetch("/api/contactForm", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          subject: form.subject.trim(),
          message: form.message.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const error = new Error(data.message || "Failed to submit contact form");
        (error as any).code = data.code;
        (error as any).status = response.status;
        throw error;
      }

      setSubmitted(true);
    } catch (err: any) {
      logError(err, {
        component: "Contact",
        action: "submitContactForm",
      });

      if (err?.code === "resource-exhausted" || err?.status === 429) {
        setServerError(
          err.message || "Too many messages sent. Please try again later.",
        );
      } else {
        setServerError(
          err?.message || "Something went wrong. Please try again or email us directly.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SimpleNavigation />

      <main id="contact" className="bg-primary min-h-screen font-serif">
        {/* Hero */}
        <section className="text-center px-6 pt-16 pb-12">
          <div className="w-20 h-20 rounded-full bg-accent-light inline-flex items-center justify-center mb-7 text-accent">
            <MessageCircle size={36} strokeWidth={1.5} />
          </div>
          <h1 className="text-5xl font-normal tracking-tight text-[#1a1a1a] mb-5">
            Get in Touch
          </h1>
          <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
            Have questions, feedback, or just want to say hi? We'd love to hear
            from you!
          </p>
        </section>

        {/* Form + Sidebar */}
        <section className="max-w-5xl mx-auto px-8 pb-20">
          <div className="flex flex-col md:flex-row gap-6">
            {/* Contact form */}
            <div className="bg-white border border-accent-light rounded-2xl p-9 flex-1">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center text-green-500">
                    <CheckCircle size={36} strokeWidth={1.5} />
                  </div>
                  <h2 className="text-xl font-semibold text-[#1a1a1a]">
                    Message Sent!
                  </h2>
                  <p className="text-sm text-[#666] max-w-xs leading-6">
                    Thanks for reaching out! We'll get back to you within 24–48
                    hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        email: "",
                        subject: "",
                        message: "",
                      });
                    }}
                    className="mt-2 text-sm text-accent hover:text-primary-dark transition font-medium"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-semibold text-[#1a1a1a] mb-7">
                    Send us a message
                  </h2>

                  <div className="flex gap-5 mb-5">
                    <div className="flex-1">
                      <label className="block text-sm text-[#333] mb-1.5">
                        Name <span className="text-accent">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your name"
                        className={`w-full border rounded-lg px-4 py-2.5 text-sm bg-primary placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-accent ${errors.name ? "border-red-400" : "border-accent-light"}`}
                      />
                      {errors.name && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.name}
                        </p>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm text-[#333] mb-1.5">
                        Email <span className="text-accent">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className={`w-full border rounded-lg px-4 py-2.5 text-sm bg-primary placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-accent ${errors.email ? "border-red-400" : "border-accent-light"}`}
                      />
                      {errors.email && (
                        <p className="text-xs text-red-500 mt-1">
                          {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="block text-sm text-[#333] mb-1.5">
                      Subject <span className="text-accent">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="What's this about?"
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm bg-primary placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-accent ${errors.subject ? "border-red-400" : "border-accent-light"}`}
                    />
                    {errors.subject && (
                      <p className="text-xs text-red-500 mt-1">
                        {errors.subject}
                      </p>
                    )}
                  </div>

                  <div className="mb-8">
                    <label className="block text-sm text-[#333] mb-1.5">
                      Message <span className="text-accent">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us what's on your mind..."
                      rows={5}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm bg-primary placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-accent resize-none ${errors.message ? "border-red-400" : "border-accent-light"}`}
                    />
                    <div className="flex justify-between mt-1">
                      {errors.message ? (
                        <p className="text-xs text-red-500">{errors.message}</p>
                      ) : (
                        <span />
                      )}
                      <p className="text-xs text-gray-400">
                        {form.message.length}/5000
                      </p>
                    </div>
                  </div>

                  {serverError && (
                    <p className="text-sm text-red-500 mb-4">{serverError}</p>
                  )}

                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 bg-accent hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-6 py-3 rounded-lg transition-colors"
                  >
                    <Mail size={16} strokeWidth={1.5} />
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-5 md:w-72">
              <div className="bg-white border border-accent-light rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-experience-bg flex items-center justify-center mb-4 text-accent">
                  <Mail size={20} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-[#1a1a1a] mb-1.5">
                  Email Us
                </h3>
                <p className="text-sm text-[#666] leading-6 mb-2">
                  For general inquiries and support
                </p>
                <a
                  href="mailto:support@groweasy.be"
                  className="text-sm text-accent hover:text-primary-dark transition font-medium"
                >
                  support@groweasy.be
                </a>
              </div>

              <div className="bg-white border border-accent-light rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-experience-bg flex items-center justify-center mb-4 text-accent">
                  <Clock size={20} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-[#1a1a1a] mb-1.5">
                  Response Time
                </h3>
                <p className="text-sm text-[#666] leading-6">
                  We typically respond within 24–48 hours during business days.
                  Thank you for your patience!
                </p>
              </div>

              <div className="bg-experience-bg border border-accent-light rounded-2xl p-6">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-4 text-accent">
                  <Bug size={20} strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-semibold text-[#1a1a1a] mb-1.5">
                  Beta Tester?
                </h3>
                <p className="text-sm text-[#666] leading-6">
                  If you're experiencing issues with the app, please include
                  details about your device and what you were doing when the
                  issue occurred.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Questions */}
        <section className="bg-primary">
          <div className="max-w-5xl mx-auto px-8 py-20">
            <h2 className="text-3xl font-normal text-[#1a1a1a] text-center mb-10">
              Common Questions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {faqs.map((faq) => (
                <div
                  key={faq.question}
                  className="bg-experience-bg border border-accent-light rounded-2xl p-7"
                >
                  <h3 className="text-base font-semibold text-[#1a1a1a] mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-sm text-[#666] leading-7">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
